import React from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  BookOpen,
  BarChart3,
  UserCircle2,
  Sparkles,
  Brain,
  Heart,
  FlaskConical,
} from "lucide-react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { useSubscriptionAccess } from "@/components/UpgradeLock";
import { Link } from "react-router-dom";
import DilBudgetMeter from "@/components/DilBudgetMeter";

/**
 * TABS — 5-секционна horizontal навигация под header-а.
 * Desktop (≥md): винаги видима като tab bar.
 * Mobile (<md): MobileBottomNav остава primary → tabs тук само за bread-crumb.
 *
 * iter420 — добавен таб "Health Intelligence" (personal health graph).
 */
const TABS = [
  { id: "dashboard",     path: "",                   icon: LayoutDashboard, bg: "Моето здраве",       en: "My Health",          es: "Mi salud" },
  { id: "nutrition-lab", path: "nutrition-lab",      icon: FlaskConical,    bg: "Хранителна лаборатория", en: "Nutrition Lab",       es: "Nutrition Lab" },
  { id: "mindbody-lab",  path: "mindbody-lab",       icon: Brain,           bg: "Ум и тяло",          en: "Mind & Body Lab",    es: "Mind & Body Lab" },  // iter486
  { id: "diary",         path: "diary",              icon: BookOpen,        bg: "Умно хранене",       en: "Smart Eating",       es: "Alimentación inteligente" },
  { id: "analyses",      path: "analyses",           icon: BarChart3,       bg: "Прецизно здраве",    en: "Precision Health",   es: "Precision Health" },
  { id: "intelligence",  path: "health-intelligence",icon: Heart,           bg: "Здравни раздели",    en: "Health Intelligence",es: "Health Intelligence" },
  // Hidden per user request — module remains in codebase, just removed from navigation menu.
  // To restore: uncomment the line below.
  // { id: "womens-health", path: "womens-health",      icon: Heart,           bg: "Женско здраве",      en: "Women's Health",     es: "Salud femenina", femaleOnly: true },
  { id: "profile",       path: "profile",            icon: UserCircle2,     bg: "Профил",             en: "Profile",            es: "Perfil" },
];


function PlanBadge({ tier, isDark, bg, lang }) {
  const cfg = {
    admin:    { label: bg ? "Admin"   : "Admin",   cls: "bg-violet-500/30 text-violet-200 border-violet-500/40" },
    active:   { label: bg ? "Pro"     : "Pro",     cls: "bg-emerald-500/30 text-emerald-200 border-emerald-500/40" },
    trialing: { label: bg ? "Trial"   : "Trial",   cls: "bg-sky-500/30 text-sky-200 border-sky-500/40" },
    free:     { label: bg ? "Free"    : "Free",    cls: "bg-gray-500/30 text-gray-300 border-gray-500/40" },
    expired:  { label: bg ? "Изтекъл" : lang === "es" ? "Expirado" : "Expired",
                cls: "bg-amber-500/30 text-amber-200 border-amber-500/40" },
  };
  const c = cfg[tier] || cfg.free;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border backdrop-blur-sm
        ${isDark ? c.cls : c.cls.replace("/30", "/15").replace("/40", "/30").replace(" text-gray-300", " text-gray-700").replace("text-violet-200","text-violet-700").replace("text-emerald-200","text-emerald-700").replace("text-sky-200","text-sky-700").replace("text-amber-200","text-amber-700")}`}
      data-testid="plan-badge"
    >
      {c.label}
    </span>
  );
}


/**
 * AppShell — shared layout за authenticated секции. Съдържа:
 *   • <Outlet /> — child page (Dashboard/Diary/Analyses/Plan/Profile)
 *   • sticky horizontal tab bar (desktop only, ≥md)
 *   • plan badge + Upgrade CTA (desktop header slot — mobile остава чист)
 *
 * Note: DashboardHeader все още e част от всяка page (legacy). Phase 2 ще
 * го вдигне на ниво AppShell.
 */
export default function AppShell() {
  const location = useLocation();
  const { user } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const { tier, isFree, isExpired } = useSubscriptionAccess();
  const lang = language === "en" ? "en" : language === "es" ? "es" : "bg";
  const bg = lang === "bg";

  // Skip tab bar при onboarding / admin / legacy пътища
  const pathname = location.pathname;
  // iter571 — Easy mode keeps a SIMPLIFIED top bar (само Dashboard + Diary +
  // Profile + plan badge), вместо да скрием цялата chrome. Това поправя
  // регрешн от iter569 където пациентът губеше всякаква top navigation.
  // Standard/Expert виждат пълния set от tabs + DIL meter.
  const showShellChrome = pathname.startsWith("/dashboard");
  // Tabs visible в Easy: основните pathways, които начинаещ потребител има нужда от.
  // #18: Admin tier — никога не показвай Upgrade CTA (admin не може да се надгражда)
  const showUpgradeCTA = tier !== "admin" && (isFree || isExpired);

  return (
    <>
      {/* Sticky tab bar + plan info — desktop only */}
      {showShellChrome && (
        <div
          className={`hidden md:block sticky top-0 z-30 border-b backdrop-blur-md
            ${isDark
              ? "bg-gradient-to-r from-[hsl(280,40%,10%)]/95 via-[hsl(265,40%,11%)]/95 to-[hsl(320,40%,11%)]/95 border-white/10"
              : "bg-gradient-to-r from-violet-50/95 via-pink-50/85 to-purple-50/90 border-violet-200/60"}`}
          data-testid="app-shell-tabs"
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-2 gap-4">
            <nav
              className="flex items-center gap-1"
              aria-label={bg ? "Основна навигация" : "Main navigation"}
            >
              {TABS.filter(t => !t.femaleOnly || (() => {
                const g = (user?.profile?.personal?.gender || "").toLowerCase();
                return user?.role === "admin" || ["female", "жена", "f"].includes(g);
              })())
                // iter598 — Easy-режим филтърът ПРЕМАХНАТ: на production менюто
                // се свиваше до 3 таба (default mode = "easy"). Режимите са
                // премахнати от Dashboard-а → пълното меню се показва винаги.
                .map(({ id, path, icon: Icon, bg: bgL, en, es }) => {
                const label = lang === "es" ? es : lang === "en" ? en : bgL;
                const to = path ? `/dashboard/${path}` : "/dashboard";
                return (
                  <NavLink
                    key={id}
                    to={to}
                    end={path === ""}
                    className={({ isActive }) =>
                      `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all
                       ${isActive
                          ? "bg-gradient-to-r from-pink-500 via-fuchsia-500 to-violet-600 text-white shadow-md shadow-violet-500/40 ring-1 ring-white/30"
                          : (isDark
                              ? "text-slate-300 bg-white/[0.04] border border-white/10 hover:text-white hover:bg-white/10 hover:border-pink-500/40"
                              : "text-slate-700 bg-white/70 border border-violet-200/70 hover:text-violet-700 hover:bg-white hover:border-pink-400/60 hover:shadow-sm")}`
                    }
                    data-testid={`app-shell-tab-${id}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {label}
                  </NavLink>
                );
              })}
            </nav>

            <div className="flex items-center gap-2.5">
              {/* DIL meter — винаги видим в shell tab area (user req Apr 26). */}
              <div className="hidden md:block">
                <DilBudgetMeter compact />
              </div>
              <PlanBadge tier={tier} isDark={isDark} bg={bg} lang={lang} />
              {showUpgradeCTA && (
                <Link
                  to="/upgrade"
                  className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-bold transition-colors
                    ${isDark
                      ? "bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white hover:from-violet-400 hover:to-fuchsia-400"
                      : "bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:from-violet-700 hover:to-fuchsia-700"}`}
                  data-testid="app-shell-upgrade-btn"
                >
                  <Sparkles className="w-3 h-3" />
                  {bg ? "Надгради" : lang === "es" ? "Mejorar" : "Upgrade"}
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      <Outlet />
    </>
  );
}
